import { LEAD_OVERALL_SCORES } from "@signalist/shared-types";
import { z } from "zod";

/** Whether a connection has been enriched into a prospect. */
export type EnrichmentFilter = "ALL" | "ENRICHED" | "NON_ENRICHED";

/**
 * The connections filter panel's state (PREC-3914).
 *
 * Every field is sent to the API and applied in SQL over the whole network — the two dropdowns this
 * replaces filtered only the rows already on screen, which is why the table appeared not to filter.
 */
export interface ConnectionsFilters {
	enrichmentFilter: EnrichmentFilter;
	scores: string[];
	countries: string[];
	seniorities: string[];
	languages: string[];
	companyIndustries: string[];
	companyCountries: string[];
	/** Index into {@link COMPANY_SIZE_BUCKETS}; empty means "any size". */
	companySizeBuckets: string[];
	companyName: string;
	jobTitle: string;
	connectedFrom: string;
	connectedTo: string;
}

export const EMPTY_CONNECTIONS_FILTERS: ConnectionsFilters = {
	enrichmentFilter: "ALL",
	scores: [],
	countries: [],
	seniorities: [],
	languages: [],
	companyIndustries: [],
	companyCountries: [],
	companySizeBuckets: [],
	companyName: "",
	jobTitle: "",
	connectedFrom: "",
	connectedTo: "",
};

/** LinkedIn's own headcount bands, so the buckets match what people read on a company page. */
export const COMPANY_SIZE_BUCKETS: { id: string; title: string; min: number; max?: number }[] = [
	{ id: "1-10", title: "1-10", min: 1, max: 10 },
	{ id: "11-50", title: "11-50", min: 11, max: 50 },
	{ id: "51-200", title: "51-200", min: 51, max: 200 },
	{ id: "201-500", title: "201-500", min: 201, max: 500 },
	{ id: "501-1000", title: "501-1,000", min: 501, max: 1000 },
	{ id: "1001-5000", title: "1,001-5,000", min: 1001, max: 5000 },
	{ id: "5001-10000", title: "5,001-10,000", min: 5001, max: 10000 },
	{ id: "10001+", title: "10,001+", min: 10001 },
];

/**
 * The overall scores a lead can carry, in the order the table shows them — every stored verdict,
 * "Unsure" ("?", PREC-4075) included, so no scored lead is unfilterable.
 */
export const SCORE_VALUES: string[] = [...LEAD_OVERALL_SCORES];

export const ConnectionsFilterOptionsSchema = z.object({
	scores: z.array(z.string()),
	countries: z.array(z.string()),
	seniorities: z.array(z.string()),
	languages: z.array(z.string()),
	companyIndustries: z.array(z.string()),
	companyCountries: z.array(z.string()),
});

export type ConnectionsFilterOptions = z.infer<typeof ConnectionsFilterOptionsSchema>;

/**
 * How many filters are set, for the badge on the Filter button.
 *
 * Counted per field rather than per selected value: "Country: Germany, Portugal" is one decision
 * the user made, and a badge reading "2" for it would misdescribe the panel.
 */
export const countActiveConnectionsFilters = (filters: ConnectionsFilters): number => {
	const lists = [
		filters.scores,
		filters.countries,
		filters.seniorities,
		filters.languages,
		filters.companyIndustries,
		filters.companyCountries,
		filters.companySizeBuckets,
	];

	const texts = [filters.companyName, filters.jobTitle, filters.connectedFrom, filters.connectedTo];

	return (
		lists.filter((list) => list.length > 0).length +
		texts.filter((text) => text !== "").length +
		(filters.enrichmentFilter === "ALL" ? 0 : 1)
	);
};

/**
 * The selected size buckets as one headcount range.
 *
 * The API takes a single min/max rather than a set of bands, so several buckets collapse into the
 * span they cover. Picking "1-10" and "10,001+" therefore means "any size", which is the honest
 * reading of a range — the alternative would be a per-bucket OR the panel does not offer.
 */
export const companySizeRange = (
	bucketIds: string[]
): { companySizeMin?: number; companySizeMax?: number } => {
	const buckets = COMPANY_SIZE_BUCKETS.filter((bucket) => bucketIds.includes(bucket.id));

	if (!buckets.length) {
		return {};
	}

	const min = Math.min(...buckets.map((bucket) => bucket.min));
	const maxima = buckets.map((bucket) => bucket.max);

	return {
		companySizeMin: min,
		// An open-ended bucket ("10,001+") in the selection means there is no upper bound.
		...(maxima.includes(undefined) ? {} : { companySizeMax: Math.max(...(maxima as number[])) }),
	};
};

/** The panel's state as request params; an unset field is left out rather than sent empty. */
export const toConnectionsFilterParams = (
	filters: ConnectionsFilters
): Record<string, string | number | undefined> => {
	const list = (values: string[]) => (values.length ? values.join(",") : undefined);
	const text = (value: string) => (value === "" ? undefined : value);

	return {
		enrichmentFilter: filters.enrichmentFilter === "ALL" ? undefined : filters.enrichmentFilter,
		scores: list(filters.scores),
		countries: list(filters.countries),
		seniorities: list(filters.seniorities),
		languages: list(filters.languages),
		companyIndustries: list(filters.companyIndustries),
		companyCountries: list(filters.companyCountries),
		companyName: text(filters.companyName),
		jobTitle: text(filters.jobTitle),
		connectedFrom: text(filters.connectedFrom),
		connectedTo: text(filters.connectedTo),
		...companySizeRange(filters.companySizeBuckets),
	};
};
